// /src/components/updated/Deposit/SelectPaymentMethod.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Button, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  root: { width: '100%' },
  label: {
    display: 'block',
    fontSize: 12,
    marginBottom: 10,
    color: theme.palette.text.secondary + ' !important',
    textAlign: 'left',
  },
  trigger: {
    width: '100%',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    textTransform: 'none',
    background: 'transparent',
    '&:hover': {
      background: 'transparent',
      borderColor: theme.palette.primary.main,
    },
  },
  yellow: {
    borderColor: '#FEBA00 !important',
    '&:hover': { borderColor: '#FEBA00 !important', opacity: 0.9 },
  },
  blue: {
    borderColor: '#07A6FC !important',
    '&:hover': { borderColor: '#07A6FC !important', opacity: 0.9 },
  },
  text: {
    color: theme.palette.text.primary,
    fontSize: 14,
    marginRight: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  helper: {
    marginTop: 6,
    marginLeft: 0,
    color: `${theme.palette.error.main} !important`,
  },
}));

/**
 * A thin wrapper that renders a styled button to open your Pay-With popup.
 * Props:
 * - onClick: () => void
 * - buttonText: string (current selection or CTA)
 * - errorMsg: boolean | string (when truthy, shows red helper)
 * - blueBorders / yellowBorders: boolean (brand accent)
 * - type: 'Buy' | 'Sell' | 'Deposit' (only affects the label)
 */
const SelectPaymentMethod = ({
  onClick,
  buttonText = 'Select Transaction Method',
  errorMsg = false,
  blueBorders = false,
  yellowBorders = false,
  type = 'Deposit',
  label = 'Select Transaction Method',
}) => {
  const classes = useStyles();

  const borderClass = blueBorders ? classes.blue : yellowBorders ? classes.yellow : '';

  return (
    <div className={classes.root}>
      <label className={classes.label}>
        {label || `Transaction Method${type ? ` (${type})` : ''}`}
      </label>
      <Button
        variant="outlined"
        className={`${classes.trigger} ${borderClass}`}
        onClick={onClick}
      >
        <span className={classes.text}>{buttonText}</span>
      </Button>
      {!!errorMsg && (
        <FormHelperText className={classes.helper}>
          {typeof errorMsg === 'string' ? errorMsg : 'Please select a method'}
        </FormHelperText>
      )}
    </div>
  );
};

SelectPaymentMethod.propTypes = {
  onClick: PropTypes.func.isRequired,
  buttonText: PropTypes.string,
  errorMsg: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  blueBorders: PropTypes.bool,
  yellowBorders: PropTypes.bool,
  type: PropTypes.string,
  label: PropTypes.string,
};

export default SelectPaymentMethod;
